import React from 'react'
import { Location } from 'react-router'

import { trace } from '@opentelemetry/api'
import { logs } from '@ui/logger'
import { withRouter } from '@ui/providers/withRouter'
import { useInstanceStore } from '@ui/stores'

const tracer = trace.getTracer('error-boundary-tracer')
const logger = logs.getLogger('error-boundary-logger')

interface ErrorBoundaryProps {
  children: React.ReactNode
  fallback: React.ReactNode | ((error: Error) => React.ReactNode)
  location?: Location
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

class ErrorBoundary_ extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const instanceKey = useInstanceStore.getState().selectedInstanceKey
    const instance = useInstanceStore.getState().instances.find((i) => i.connect_key === instanceKey)?.name
    logger.emit({
      timestamp: new Date(),
      severityNumber: 17,
      severityText: 'ERROR',
      body: error.message,
      attributes: {
        stack: error.stack,
        component: errorInfo.componentStack,
        location: window.location.href,
        instanceId: instance ?? '',
      },
    })

    const span = tracer.startSpan('ErrorBoundary caught error')

    try {
      span.recordException(error)
      span.setAttributes({
        message: error.message,
        stack: error.stack,
        component: errorInfo.componentStack ?? '',
        location: window.location.href,
        instanceId: instance ?? '',
      })
      span.setStatus({ code: 2, message: 'Unhandled React Error' }) // Unhandled React Error
    } finally {
      span.end()
    }
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (this.props.location !== prevProps.location) {
      this.setState({ hasError: false, error: null }) // Reset error boundary state on route change
    }
  }

  render() {
    if (this.state.hasError && this.state.error) {
      if (typeof this.props.fallback === 'function') {
        return this.props.fallback(this.state.error)
      }
      return React.cloneElement(this.props.fallback as React.ReactElement, { error: this.state.error })
    }

    return this.props.children
  }
}

export const ErrorBoundary = withRouter(ErrorBoundary_)
